import { pdf } from '@react-pdf/renderer'
import dayjs from 'dayjs'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { type NiiReportFields } from 'features/libra/stepComponents/niiReport/niiReportApi'
import {
  drawZonesOnCanvas,
  ZONE_COLORS,
} from 'features/libra/stepComponents/niiReport/zoneUtils'
import { useLibraAssetModelData } from 'features/libra/stepComponents/useLibraAssetModelData'
import {
  useZoneData,
  type ZoneParams,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import {
  ELIGIBILITY_STATUS,
  type EligibilityStatus,
} from 'features/libra/types/eligibility'
import { useRolodexEntries, useRolodexSignedURLs } from 'features/rolodex'
import { useCallback, useMemo, useState } from 'react'
import {
  RolodexEntryPutJson,
  RolodexEntryType,
  type RolodexFilePath,
} from 'types'
import { downloadBlob } from 'utils/download'
import { fetchImageAsBase64 } from 'utils/imageUtils'
import { v4 } from 'uuid'

import {
  type NiiPdfZoneImage,
  NiiReportPdfDocument,
  type NiiReportPdfProps,
} from './NiiReportPdfDocument'

function buildDownloadFilename(
  siteName: string | undefined,
  assetName: string | undefined
): string {
  const parts = [siteName, assetName, 'NII-Report'].filter(Boolean)
  return `${parts.join('-')}.pdf`
}

function resolveProps(
  fieldValues: FieldValues,
  fields: NiiReportFields,
  siteName: string | undefined,
  assetName: string | undefined,
  analysisVersion: number,
  zones: ReadonlyArray<{
    title: string
    color?: string
    zone_recommendations?: ReadonlyArray<string>
  }>,
  zoneImages: ReadonlyArray<NiiPdfZoneImage>
): NiiReportPdfProps {
  const eligibility =
    unwrapFieldValue<EligibilityStatus>(fieldValues[fields.eligibility]) ??
    ELIGIBILITY_STATUS.UNCLEAR

  const recommendations =
    unwrapFieldValue<ReadonlyArray<string>>(
      fieldValues[fields.recommendations]
    ) ?? []

  const justification = fields.justification
    ? unwrapFieldValue<string>(fieldValues[fields.justification])
    : undefined

  return {
    siteName,
    assetName,
    analysisDate: unwrapFieldValue<string>(fieldValues[fields.analysisDate]),
    analysisVersion,
    executiveSummary: unwrapFieldValue<string>(
      fieldValues[fields.executiveSummary]
    ),
    internalComments: unwrapFieldValue<string>(
      fieldValues[fields.internalComments]
    ),
    eligibility,
    recommendations,
    justification,
    zones: zones
      .filter(
        (z) => z.zone_recommendations && z.zone_recommendations.length > 0
      )
      .map((z, index) => ({
        title: z.title,
        color: z.color ?? ZONE_COLORS[index % ZONE_COLORS.length],
        zone_recommendations: z.zone_recommendations ?? [],
      })),
    zoneImages,
  }
}

export interface UseNiiPdfExportResult {
  handleGenerateAndDownload: () => Promise<void>
  isGenerating: boolean
}

export function useNiiPdfExport(
  fields: NiiReportFields,
  zoneFieldName: string = 'zones'
): UseNiiPdfExportResult {
  const { fieldValues, ticketId, ticketVersion } = useTicketAccess()
  const { siteName, unit } = useLibraAssetModelData()
  const { zones } = useZoneData(zoneFieldName)
  const { rolodexPut, rolodexUploadFile } = useRolodexEdit()
  const [isGenerating, setIsGenerating] = useState(false)

  const assetName = unit?.data.name as string | undefined

  // ── Existing NII report entry for this ticket ───────────────────────
  const existingReportQuery = useMemo(
    () =>
      ticketId
        ? {
            type: RolodexEntryType.REPORT,
            tags: { [RolodexEntryType.LIBRA_TICKET]: ticketId },
          }
        : undefined,
    [ticketId]
  )
  const { reports: existingReports } = useRolodexEntries(existingReportQuery, {
    swrOptions: { revalidateOnFocus: false },
  })

  // ── Zone reference images ───────────────────────────────────────────
  const zoneImageIds = useMemo(() => {
    const ids = zones
      .map((z) => z.zone_reference_image_id)
      .filter((id): id is string => !!id)
    return [...new Set(ids)]
  }, [zones])

  const imageEntriesQuery = useMemo(
    () =>
      zoneImageIds.length > 0
        ? { ids: zoneImageIds, type: [RolodexEntryType.REPORT] }
        : undefined,
    [zoneImageIds]
  )
  const { reports: imageEntries } = useRolodexEntries(imageEntriesQuery, {
    swrOptions: { revalidateOnFocus: false },
  })

  const imageFileQueries = useMemo<
    ReadonlyArray<RolodexFilePath> | undefined
  >(() => {
    const entries = imageEntries
      .map((doc) => {
        const filename = doc.raw.data.filename as string | undefined
        if (!filename) return null
        return { entry_id: doc.id, path: filename }
      })
      .filter(Boolean) as ReadonlyArray<RolodexFilePath>
    return entries.length > 0 ? entries : undefined
  }, [imageEntries])

  const { results: imageSignedResults } = useRolodexSignedURLs(imageFileQueries)

  const resolvedZoneImages = useMemo(() => {
    const urlMap = new Map(
      imageSignedResults.map((r) => [r.entry_id, r.signed_url])
    )

    const zonesByImage = new Map<string, ReadonlyArray<ZoneParams>>()
    for (const imageId of zoneImageIds) {
      const imageZones = zones.filter(
        (z) =>
          z.zone_reference_image_id === imageId &&
          z.drawing_bounds !== undefined
      )
      zonesByImage.set(imageId, imageZones)
    }

    return imageEntries
      .map((doc) => {
        const url = urlMap.get(doc.id)
        if (!url) return null
        const filename = doc.raw.data.filename as string | undefined
        return {
          id: doc.id,
          name: doc.data.visibleName || filename || doc.id.substring(0, 8),
          url,
          zones: zonesByImage.get(doc.id) ?? [],
        }
      })
      .filter(Boolean) as ReadonlyArray<{
      id: string
      name: string
      url: string
      zones: ReadonlyArray<ZoneParams>
    }>
  }, [imageEntries, imageSignedResults, zoneImageIds, zones])

  // ── Filenames / download ────────────────────────────────────────────
  const downloadFilename = useMemo(
    () => buildDownloadFilename(siteName, assetName),
    [siteName, assetName]
  )

  const handleGenerateAndDownload = useCallback(async () => {
    setIsGenerating(true)
    try {
      const zoneImages = await Promise.all(
        resolvedZoneImages.map(async (img) => {
          try {
            const dataUrl = await fetchImageAsBase64(
              img.url,
              undefined,
              (ctx, imgEl) =>
                drawZonesOnCanvas(ctx, imgEl.naturalHeight, img.zones)
            )
            return { name: img.name, dataUrl }
          } catch {
            return null
          }
        })
      ).then(
        (results) => results.filter(Boolean) as ReadonlyArray<NiiPdfZoneImage>
      )

      const props = resolveProps(
        fieldValues,
        fields,
        siteName,
        assetName,
        ticketVersion,
        zones,
        zoneImages
      )

      const blob = await pdf(<NiiReportPdfDocument {...props} />).toBlob()

      const reportAlreadyExists = existingReports.some(
        (report) =>
          report.raw.data.nii_report === true &&
          report.raw.data.analysis_version === ticketVersion
      )
      if (reportAlreadyExists) {
        // Only download
        await downloadBlob(blob, downloadFilename)
      } else {
        const tags: Record<string, string> = {}
        if (unit) {
          const nonNullTags = Object.fromEntries(
            Object.entries(unit.tags).filter(
              ([, v]) => v !== null && v !== undefined
            ) as [string, string][]
          )
          Object.assign(tags, nonNullTags)
          tags.unit = unit.id
        }
        tags[RolodexEntryType.LIBRA_TICKET] = ticketId

        const documentId = v4()
        const entry: RolodexEntryPutJson = {
          id: documentId,
          type: RolodexEntryType.REPORT,
          data: {
            nii_report: true,
            analysis_version: ticketVersion,
            filename: downloadFilename,
            size: blob.size,
            file_type: 'pdf',
            upload_date: dayjs().toISOString(),
            visible_name: downloadFilename,
            fields: {},
          },
          tags,
          index: {},
        }
        const uploadAndSave = async () => {
          await rolodexPut([entry])

          await rolodexUploadFile(documentId, blob, downloadFilename)
        }

        await Promise.all([
          downloadBlob(blob, downloadFilename),
          uploadAndSave(),
        ])
      }
    } finally {
      setIsGenerating(false)
    }
  }, [
    resolvedZoneImages,
    fieldValues,
    fields,
    siteName,
    assetName,
    ticketVersion,
    zones,
    existingReports,
    unit,
    ticketId,
    rolodexPut,
    rolodexUploadFile,
    downloadFilename,
  ])

  return { handleGenerateAndDownload, isGenerating }
}
